import { isEmpty } from 'lodash'
import React from 'react'
import { SourceInsights } from '../../../lib/types/SourceInsights'
import { formatNumber, formatPercentage } from '../../../lib/utils/formatNumber'
import classNames from 'classnames'
import PolarAreaChart from '../../../components/Charts/PolarAreaChart'
import { HighlightCard } from '@demandlocal/components'
import HorizontalBarChart from '../../Charts/HorizontalBarChart'

const LeadsInsights = ({
  insights,
  chartTotal,
  isLoading,
  isPreviousData,
}: {
  chartTotal: number
  insights?: SourceInsights
  isLoading: boolean
  isPreviousData: boolean
}): JSX.Element => {
  return (
    <div className="flex flex-col gap-4">
      <span className="font-bold pt-10">Insights</span>

      {!isEmpty(insights?.sales) && (
        <div className={classNames('grid grid-cols-2 gap-8')}>
          <HighlightCard
            isLoading={isLoading}
            isPrevious={isPreviousData}
            value={insights?.sales?.total_sales}
            content={
              <span>
                number of sold vehicles that occured after a lead submission, with an
                average turnaround of{' '}
                <strong>
                  {formatNumber(insights?.sales?.average_time_diff ?? 0, {
                    maximumFractionDigits: 1,
                  })}{' '}
                  days between lead and sale
                </strong>
              </span>
            }
          />
          <HighlightCard
            isLoading={isLoading}
            isPrevious={isPreviousData}
            value={formatNumber(insights?.sales?.lead_sales ?? 0)}
            content={
              <span>number of sales for the vehicle the user submitted a lead for</span>
            }
          />
        </div>
      )}
      <div className={classNames('grid grid-cols-2 gap-8')}>
        <div className="w-full">
          <HighlightCard
            isLoading={isLoading}
            isPrevious={isPreviousData}
            value={formatNumber(insights?.unknownCustomers?.noEmailCount ?? 0)}
            content={
              <span>
                of the leads{' '}
                {chartTotal > 0
                  ? `(${formatPercentage(
                      (insights?.unknownCustomers?.noEmailCount || 0) / chartTotal,
                      0,
                    )})`
                  : ''}{' '}
                do not have an email associated with it
              </span>
            }
          />
        </div>
        <div className="w-full">
          <HighlightCard
            isLoading={isLoading}
            isPrevious={isPreviousData}
            value={formatNumber(insights?.unknownCustomers?.noPhoneCount ?? 0)}
            content={
              <span>
                of the leads{' '}
                {chartTotal > 0
                  ? `(${formatPercentage(
                      (insights?.unknownCustomers?.noPhoneCount || 0) / chartTotal,
                      0,
                    )})`
                  : ''}{' '}
                do not have a phone number associated with it
              </span>
            }
          />
        </div>
      </div>
      <div
        className={classNames('grid grid-cols-2 gap-8', {
          'opacity-50': isLoading || isPreviousData,
        })}>
        <HighlightCard
          isLoading={isLoading}
          isPrevious={isPreviousData}
          value={formatNumber(insights?.uniqueCustomers ?? 0)}
          content={
            <span>
              unique customers submitted at least one lead, with an average of{' '}
              <strong>
                {insights?.uniqueCustomers && insights?.uniqueCustomers > 0
                  ? formatNumber(chartTotal / insights?.uniqueCustomers || 0, {
                      maximumFractionDigits: 1,
                    })
                  : 0}{' '}
                leads/person
              </strong>
            </span>
          }
        />
        <HighlightCard
          isLoading={isLoading}
          isPrevious={isPreviousData}
          value={formatNumber(insights?.exposedLeadCustomers?.length ?? 0)}
          content={
            <span>
              of the {formatNumber(insights?.uniqueCustomers ?? 0)} people who submitted a
              lead were exposed to an ad in the last 90 days, with an average of{' '}
              <strong>
                {insights?.exposedLeadCustomers?.length
                  ? formatNumber(
                      insights?.exposedLeadCustomers?.reduce(
                        (acc, cur) => acc + Number(cur.impressions),
                        0,
                      ) / insights?.exposedLeadCustomers?.length,
                      { maximumFractionDigits: 1 },
                    )
                  : 0}{' '}
                impressions/person
              </strong>
            </span>
          }
        />
      </div>
      <div className={'flex grid grid-cols-2 gap-5'}>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Leads Vehicle States</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <PolarAreaChart
              data={
                insights?.vehicleStatus?.map((d) => ({
                  label: d.status,
                  value: d.items,
                })) ?? []
              }
              isLoading={isLoading}
              isPreviousData={isPreviousData}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Leads Vehicle Makes</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <PolarAreaChart
              data={
                insights?.vehicleMake?.slice(0, 5)?.map((d) => ({
                  label: d.make,
                  value: d.items,
                })) ?? []
              }
              isLoading={isLoading}
              isPreviousData={isPreviousData}
            />
          </div>
        </div>
      </div>
      {Boolean(insights?.leadSources?.length) && (
        <div className={'min-h-[300px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Lead Sources</span>
          </div>
          <div className={'h-[300px] p-2'}>
            <HorizontalBarChart
              data={
                insights?.leadSources?.slice(0, 10)?.map((data) => {
                  return {
                    label: data.source,
                    value: data.items,
                  }
                }) ?? []
              }
              isPreviousData={isPreviousData}
              isLoading={isLoading}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default LeadsInsights
